import { Platform } from 'react-native';

interface AccessibilityPropsInput {
  accessibilityLabel?: string;
  accessibilityHint?: string;
}

interface AccessibilityPropsOutput {
  accessibilityLabel?: string;
  accessibilityHint?: string;
}

/**
 * A hook that normalizes accessibility props for cross-platform use.
 *
 * The Problem:
 *   `accessibilityHint` is a React Native concept with no ARIA equivalent on web.
 *   React Native Web silently drops `accessibilityHint`, meaning screen readers
 *   on web (NVDA, JAWS, VoiceOver for macOS) never announce the hint text.
 *
 * The Fix:
 *   On web, the hint is merged into the label as a comma-separated suffix:
 *     "Submit form, Double tap to confirm"
 *   On mobile (iOS/Android), both props are returned unchanged so that
 *   VoiceOver and TalkBack can announce them separately as intended.
 *
 * Usage:
 *   const a11yProps = useAccessibilityProps({ accessibilityLabel: 'Submit', accessibilityHint: 'Double tap to confirm' });
 *   <TouchableOpacity {...a11yProps} />
 */
export function useAccessibilityProps({
  accessibilityLabel,
  accessibilityHint,
}: AccessibilityPropsInput): AccessibilityPropsOutput {
  if (Platform.OS === 'web') {
    // On web, accessibilityHint has no ARIA equivalent — merge it into the label.
    const mergedLabel =
      accessibilityLabel && accessibilityHint
        ? `${accessibilityLabel}, ${accessibilityHint}`
        : accessibilityLabel ?? accessibilityHint;

    return { accessibilityLabel: mergedLabel };
  }

  // On mobile, return both props as-is for native screen readers.
  return { accessibilityLabel, accessibilityHint };
}